import React, { useEffect, useState, FormEvent, ChangeEvent } from 'react';
import { useAuth } from '../services/AuthContext';
import { useApi } from '../services/api';
import { Organization, CreateOrganizationData } from '../types';
import { toast } from 'react-toastify';

// Define a more specific type for the form data, excluding 'createdBy' which shouldn't be edited by the user.
type OrganizationFormData = Partial<Omit<CreateOrganizationData, 'createdBy'>>;

const OrgAdminSettings: React.FC = () => {
  const { user } = useAuth();
  const { getOrganization, updateOrganization } = useApi();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [formData, setFormData] = useState<OrganizationFormData>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      if (user?.organizationId) {
        try {
          setLoading(true);
          const orgDetails = await getOrganization(user.organizationId);
          setOrganization(orgDetails);
          if (orgDetails) {
            const { name, website, industry, size, description, address, city, state, country, postalCode } = orgDetails;
            setFormData({ name, website, industry, size, description, address, city, state, country, postalCode });
          }
          setError(null);
        } catch (err) {
          console.error('Error fetching organization details:', err);
          setError('Failed to load organization details. Please try again later.');
        }
        setLoading(false);
      } else {
        setError('Organization ID not found for the current user.');
        setLoading(false);
      }
    };

    fetchOrganizationDetails();
  }, [user, getOrganization]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user?.organizationId || !organization) {
      toast.error('Cannot save settings: Organization ID or details are missing.');
      return;
    }

    if (!formData.name?.trim()) {
        toast.error('Organization name cannot be empty.');
        return;
    }

    setSaving(true);
    try {
      // Construct the payload with only the fields present in formData and are part of OrganizationFormData
      const updatePayload: OrganizationFormData = {};
      for (const key in formData) {
        if (Object.prototype.hasOwnProperty.call(formData, key)) {
          // Type assertion for the key
          const formKey = key as keyof OrganizationFormData;
          // Only include keys that are defined in OrganizationFormData to avoid sending extra fields
          if (formKey in formData) { 
            updatePayload[formKey] = formData[formKey];
          }
        }
      }

      const updatedOrg = await updateOrganization(user.organizationId, updatePayload as Partial<CreateOrganizationData>); // Cast for the API
      setOrganization(updatedOrg);
      if (updatedOrg) {
        const { name, website, industry, size, description, address, city, state, country, postalCode } = updatedOrg;
        setFormData({ name, website, industry, size, description, address, city, state, country, postalCode });
      }
      toast.success('Organization settings updated successfully!');
    } catch (err) {
      console.error('Error updating organization settings:', err);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading settings...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  if (!organization) {
    return <div className="p-4">No organization data found.</div>;
  }

  const fields: Array<{ name: keyof OrganizationFormData; label: string; type: string; required?: boolean; options?: string[] }> = [
    { name: 'name', label: 'Organization Name', type: 'text', required: true },
    { name: 'website', label: 'Website', type: 'url' },
    { name: 'industry', label: 'Industry', type: 'text' },
    { name: 'size', label: 'Organization Size', type: 'select', options: ['1-10', '11-50', '51-200', '201-500', '500+'] },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'address', label: 'Address', type: 'text' },
    { name: 'city', label: 'City', type: 'text' },
    { name: 'state', label: 'State/Province', type: 'text' },
    { name: 'country', label: 'Country', type: 'text' },
    { name: 'postalCode', label: 'Postal Code', type: 'text' },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Organization Settings</h1>
      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {fields.map(field => (
          <div key={field.name}> 
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                id={field.name}
                name={field.name}
                rows={3}
                value={formData[field.name] || ''}
                onChange={handleChange}
                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            ) : field.type === 'select' ? (
              <select
                id={field.name}
                name={field.name}
                value={formData[field.name] || ''}
                onChange={handleChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="">Select {field.label.toLowerCase()}</option>
                {field.options?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input
                type={field.type}
                id={field.name}
                name={field.name}
                value={formData[field.name] || ''}
                onChange={handleChange}
                required={field.required}
                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            )}
          </div>
        ))}
        
        <div>
          <p className="text-sm text-gray-600">Subscription Status: <span className="font-medium">{organization.subscriptionStatus || 'N/A'}</span></p>
          <p className="text-sm text-gray-600">Current Plan: <span className="font-medium">{organization.subscriptionPlan || 'N/A'}</span></p>
        </div>

        <div className="pt-5">
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving || loading}
              className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OrgAdminSettings; 